import { Tooltip, Button as ButtonAnt, message } from 'antd';
import { useDropzone } from 'react-dropzone';
import { DragnDrop } from '../../../components/assets/DragnDrop';
import { useEffect, useRef, useState } from 'react';
import { RecycleBin } from '../../../components/assets';
import { AntModal, Button, Loading } from '../../../components';
import DownloadIcon from '../../../components/assets/DownloadIcon';
import { BaseRecord } from '../../../interfaces/BaseRecord';
import DeleteBCPFile from './modals/DeleteBCPFile';

import {
  deleteFile,
  fetchFiles,
  uploadFile,
} from '../../../services/file.services';
import { useForm } from 'react-hook-form';
import { ApolloError, useMutation } from '@apollo/client';
import { EDIT_SUPPLIER } from '../queries/suppliers';
import InputField from '../../../components/generic/InputField/InputField';
import QueryErrorBoundary from '../../../components/error-handling';

type BCPFileProps = {
  isBCPModalOpen: boolean;
  handleCloseBCPModal: () => void;
  onCancel: () => void;
  recordToEdit: BaseRecord | null;
  acceptedFile: File | null;
  updateAcceptedFile: (file: File | null) => void;
  currentFile: any | null;
  updateCurrentFile: (file: File | null) => void;
};

const BCPFile = ({
  isBCPModalOpen,
  handleCloseBCPModal,
  onCancel,
  recordToEdit,
  acceptedFile,
  updateAcceptedFile,
  currentFile,
  updateCurrentFile,
}: BCPFileProps) => {
  const [isDeleteBCPModalOpen, setIsDeleteBCPModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApolloError | null>(null);

  const buttonRef = useRef(null);

  const [editSupplier, { loading: loadingBCP, error: errorBCP }] =
    useMutation(EDIT_SUPPLIER);

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm();

  const handleShowBCPDeleteModal = () => {
    setIsDeleteBCPModalOpen(true);
  };

  const handleCloseBCPDeleteModal = () => {
    setIsDeleteBCPModalOpen(false);
  };

  const handleCancel = () => {
    if (recordToEdit?.bcpUpdatedAt || !currentFile.length) {
      handleCloseBCPModal();
      return;
    }
    if (!recordToEdit?.bcpUpdatedAt && getValues('bcpUpdatedAt') !== '') return;

    const button = buttonRef.current as unknown as HTMLButtonElement;
    if (button) {
      button.click();
      message.warning('Please update the BCP date before closing the modal');
    }
  };

  const onSubmit = async (data: any) => {
    if (
      !recordToEdit ||
      data.bcpUpdatedAt === recordToEdit?.bcpUpdatedAt?.slice(0, 10)
    ) {
      onCancel();
      return;
    }

    try {
      const response = await editSupplier({
        variables: {
          data: { bcpUpdatedAt: data?.bcpUpdatedAt, uuid: recordToEdit?.uuid },
        },
      });

      if (response.data) onCancel();
    } catch (e) {
      console.log('Mutation error', e);
    }
  };

  const handleFileUpload = async () => {
    if (!acceptedFile) return;

    setIsLoading(true);

    try {
      await uploadFile(recordToEdit?.uuid, acceptedFile);
      updateAcceptedFile(null);
    } catch (error) {
      console.log('Error uploading file', error);
      if (error instanceof ApolloError) setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileDelete = async () => {
    if (!currentFile[0]) return;

    setIsLoading(true);

    try {
      await deleteFile(recordToEdit?.uuid, currentFile[0]?.name);
      updateCurrentFile(null);
      handleCloseBCPDeleteModal();
    } catch (error) {
      console.log('Error deleting file', error);
      if (error instanceof ApolloError) setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (acceptedFile) return;

    if (!recordToEdit) {
      updateCurrentFile(null);
      return;
    }

    setIsLoading(true);

    const fetchBCP = async () => {
      try {
        const data = await fetchFiles(recordToEdit?.uuid);
        updateCurrentFile(data);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error instanceof ApolloError) setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBCP();
  }, [recordToEdit, acceptedFile]);

  return (
    <>
      <AntModal
        openModal={isBCPModalOpen}
        onCancel={handleCancel}
        title={currentFile?.length > 0 ? 'BCP File Summary' : 'Upload BCP File'}
      >
        <QueryErrorBoundary
          error={[error as ApolloError, errorBCP as ApolloError].filter(
            Boolean,
          )}
        >
          <BCPFileForm
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            handleFileUpload={handleFileUpload}
            currentFile={currentFile}
            acceptedFile={acceptedFile}
            updateAcceptedFile={updateAcceptedFile}
            handleShowBCPDeleteModal={handleShowBCPDeleteModal}
            register={register}
            control={control}
            errors={errors}
            buttonRef={buttonRef}
            reset={reset}
            recordToEdit={recordToEdit}
            loading={loadingBCP || isLoading}
          />
        </QueryErrorBoundary>
      </AntModal>
      <DeleteBCPFile
        isDeleteBCPModalOpen={isDeleteBCPModalOpen}
        handleCloseBCPDeleteModal={handleCloseBCPDeleteModal}
        onDelete={handleFileDelete}
        loading={isLoading}
        error={error}
      />
    </>
  );
};

export default BCPFile;

type BCPFileFormProps = {
  handleSubmit: any;
  onSubmit: any;
  handleFileUpload: any;
  currentFile: any;
  acceptedFile: any;
  updateAcceptedFile: (file: File) => void;
  handleShowBCPDeleteModal: () => void;
  register: any;
  control: any;
  errors: any;
  buttonRef: any;
  reset: any;
  recordToEdit: BaseRecord | null;
  loading: boolean;
};

const BCPFileForm = ({
  handleSubmit,
  onSubmit,
  handleFileUpload,
  currentFile,
  acceptedFile,
  updateAcceptedFile,
  handleShowBCPDeleteModal,
  register,
  control,
  errors,
  buttonRef,
  reset,
  recordToEdit,
  loading,
}: BCPFileFormProps) => {
  const onDrop = (acceptedFiles: File[]) => {
    updateAcceptedFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
  });

  useEffect(() => {
    const defaultValues = {
      supplierBCP:
        (currentFile && currentFile[0]?.name) || acceptedFile?.name || '',
      bcpUpdatedAt: recordToEdit?.bcpUpdatedAt?.slice(0, 10) || '',
    };
    reset(defaultValues);
  }, [currentFile, acceptedFile]);

  if (loading) return <Loading />;

  return (
    <form className="flex flex-col w-full" onSubmit={handleSubmit(onSubmit)}>
      <InputField
        label="Supplier BCP"
        type="text"
        name="supplierBCP"
        register={register}
        control={control}
        disabled
      >
        {currentFile?.length > 0 && (
          <div className="flex items-center gap-3 absolute right-2 top-1/2 -translate-y-1/2">
            <Tooltip title="Download">
              <a
                href={currentFile[0]?.url}
                target="_blank"
                rel="noreferrer"
                className="text-primary-100 font-normal text-sm"
              >
                <DownloadIcon
                  className="cursor-pointer hover:fill-blue"
                  fill="gray"
                />
              </a>
            </Tooltip>
            <Tooltip title="Delete">
              <RecycleBin
                className="cursor-pointer  hover:fill-[#EF4444]"
                fill="gray"
                onClick={handleShowBCPDeleteModal}
              />
            </Tooltip>
          </div>
        )}
      </InputField>

      {currentFile?.length > 0 && (
        <InputField
          label="Supplier BCP Update"
          type="date"
          name="bcpUpdatedAt"
          register={register}
          control={control}
          rules={{ required: true }}
          errors={errors.bcpUpdatedAt}
          errorMessage="This field is required"
        />
      )}

      {(currentFile?.length <= 0 || !currentFile) && (
        <div className="inline-flex flex-row flex-nowrap items-center my-2 justify-between">
          <div
            className="w-full bg-[#F5F5F5] rounded-md border border-[#CCCCCC] flex justify-center items-center min-h-[100px]"
            {...getRootProps()}
          >
            <input {...getInputProps({})} />
            <div className="flex flex-col gap-2.5 items-center justify-center text-[#979797] text-sm">
              <DragnDrop />
              Click or drag BCP to this area to upload
            </div>
          </div>
        </div>
      )}

      {currentFile && currentFile.length > 0 && (
        <div className="ml-auto">
          <ButtonAnt
            htmlType="submit"
            className="mt-4 py-1 text-sm font-black"
            ref={buttonRef}
          >
            Ok
          </ButtonAnt>
        </div>
      )}

      {(currentFile?.length <= 0 || !currentFile) && (
        <div className="w-[165px] mx-auto">
          <Button
            type="add"
            className="mt-8 py-1 text-sm font-black"
            onClick={handleFileUpload}
            disabled={!acceptedFile}
          >
            Upload
          </Button>
        </div>
      )}
    </form>
  );
};
